import { z } from 'zod';
import { CurrencySchema, ExpenseStatusSchema } from './enums.js';
import { PaginationMetaSchema } from './pagination.js';

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'investmentDate must be YYYY-MM-DD');

export const InvestmentSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  parcelId: z.string().uuid(),
  categoryId: z.string().uuid(),
  categoryCode: z.string(),
  categoryLabel: z.string(),
  amount: z.number(),
  currency: CurrencySchema,
  exchangeRateToMdl: z.number().nullable(),
  investmentDate: z.string(),
  description: z.string().nullable(),
  status: ExpenseStatusSchema,
  mapCount: z.number().int(),
  imageCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type Investment = z.infer<typeof InvestmentSchema>;

export const InvestmentCreateInputSchema = z
  .object({
    parcelId: z.string().uuid(),
    categoryId: z.string().uuid(),
    amount: z.number().positive('Amount must be positive'),
    currency: CurrencySchema,
    investmentDate: isoDate,
    description: z.string().max(2000).nullable().optional(),
    exchangeRateToMdl: z.number().positive().nullable().optional(),
    status: ExpenseStatusSchema.default('DRAFT'),
  })
  .strict();
export type InvestmentCreateInput = z.infer<typeof InvestmentCreateInputSchema>;

export const InvestmentUpdateInputSchema = InvestmentCreateInputSchema
  .omit({ parcelId: true })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  });
export type InvestmentUpdateInput = z.infer<typeof InvestmentUpdateInputSchema>;

export const InvestmentListQuerySchema = z.object({
  parcelId: z.string().uuid(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.enum(['investmentDate', 'amount', 'createdAt']).default('investmentDate'),
  order: z.enum(['asc', 'desc']).default('desc'),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  categoryId: z.string().uuid().optional(),
  status: z.enum(['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED']).optional(),
  includeDeleted: z.coerce.boolean().optional(),
});
export type InvestmentListQuery = z.infer<typeof InvestmentListQuerySchema>;

export const InvestmentListResponseSchema = z.object({
  items: z.array(InvestmentSchema),
  pagination: PaginationMetaSchema,
  totals: z.object({
    totalMdl: z.number(),
    totalEur: z.number(),
    count: z.number().int(),
  }),
});
export type InvestmentListResponse = z.infer<typeof InvestmentListResponseSchema>;
